import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { useTranslation } from "@coworker/locales";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import FixaPill from "@coworker/apprestructured/src/shared/wrappers/FixaPill/FixaPill";
import styles from "./SelectNumberCreatorFragment.module.css";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";

export type SelectNumberCreatorFragmentConfigProps = {
  title: string;
  field: string;
  options: number[];
  visibility?: VisibilityCondition;
  required?: boolean;
};

type SelectNumberCreatorFragmentProps = {
  configProps: SelectNumberCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SelectNumberCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectNumberCreatorFragmentProps) {
  const { field, title, options, visibility, required } = configProps;
  const { blockInput, task, onTaskChange, showValidation } = commonProps;
  const { t } = useTranslation();

  const rawValue = getByPath(task, field) as number | Loading | undefined;

  const handleOptionClick = useCallback(
    (value: number) => {
      if (blockInput) return;
      onTaskChange(setByPath(field, value));
    },
    [field, onTaskChange, blockInput]
  );

  const isVisible =
    !visibility || isLoading(task) || shouldShowField(task, visibility);

  const hasValue = !isLoading(rawValue) && typeof rawValue === "number";
  const valueIsValidOption = hasValue && options.includes(rawValue);
  // required=true (default): must select a valid option
  // required=false: optional, but if filled must be valid
  const isRequired = required ?? true;
  const valid =
    !isVisible ||
    (isRequired ? valueIsValidOption : !hasValue || valueIsValidOption);

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (!isVisible) {
    return null;
  }

  if (isLoading(rawValue)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <FixaListViewItem
      inset
      title={t(title)}
      addOn={
        showValidation && !valid ? (
          <ErrorAddOn>{t("pleaseSelectString")}</ErrorAddOn>
        ) : null
      }
      description={
        <div className={styles["options"]}>
          {options.map((option) => (
            <FixaPill
              key={option}
              label={String(option)}
              selected={rawValue === option}
              onClick={() => handleOptionClick(option)}
              disabled={blockInput}
              size="small"
            />
          ))}
        </div>
      }
    />
  );
}

export default SelectNumberCreatorFragment;
